import React, { useState, useMemo, useContext, useRef } from "react";
import { Grid, Divider, Typography, Box, Tooltip, makeStyles, Fab, IconButton } from "@material-ui/core";
import ReceiptIcon from "@material-ui/icons/ReceiptTwoTone";
import FileDownloadIcon from "@material-ui/icons/GetApp";
import GroupIcon from "@material-ui/icons/Group";
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import PersonAddDisabledIcon from '@material-ui/icons/PersonAddDisabled';

import Pagination, { initialPage, initialPageSize, getOffset } from "../components/Pagination";
import ValidatedInput from "../components/ValidatedInput";
import DateRangeInput from "../components/DateRangeInput";
import InformativeCard from "../components/InformativeCard";
import PostAddIcon from "@material-ui/icons/PostAdd";
import { ErrorSnackbar, SuccessSnackbar } from "../components/Snackbars";
import { AppContext } from "../context/AppContext";
import { useRegisteredProvidersCards } from "./useRegisteredProvidersCards";
import { useUnregisteredIssuersCards } from "./useUnregisteredIssuersCards";
import translate from "../i18n/Translator";
import { NonIntegratedCfdi, NonIntegratedCfdiParams } from "../model/CfdisNonIntegrated";
import { exportExcel } from "../api/NonIntegratedCfdisApi";
import CustomBackdrop from "../components/CustomBackdrop";
import ConnectorObjectExporter from "../connectors/ConnectorObjectExporter";
import { Family } from "../model/Connector";
import Gridable from "../components/Gridable";
import { Link } from "@material-ui/core";
import UnloadedInvoicesFilters from "./UnloadedInvoicesFilters";
import { useRegisteredProvidersGrid } from "./useRegisteredProvidersGrid";
import { useUnregisteredIssuersGrid } from "./useUnregisteredIssuersGrid";
import TaxBreakdownModal from "./modalboxes/TaxBreakdownModal";
import { useSimpleTaxBreakdownModal } from "./modalboxes/useTaxBreakdownModal";
import { hasDataResults, onAddProvider } from "./nonIntegratedCfdisUtils";
import { useHistory, useLocation } from "react-router-dom";
import Surface from "../components/Surface";

const useStyles = makeStyles((theme) => ({
    gridCell: {
        padding: theme.spacing(1, 2) + ' !important', 
    },
    clickableCell: {
        cursor: "pointer",
        color: theme.palette.primary.main,
        textDecoration: "none",
        fontWeight: 500,
        textAlign: "left",
        justify: "flex-start",
        "&:hover": {
            textDecoration: "underline"
        },
    },
    rfcCell: {
        fontFamily: "monospace",
        fontSize: "0.875rem",
        textAlign: "left",
        justify: "flex-start"
    },
    amountCell: {
        fontWeight: 500,
        textAlign: "right",
        justify: "flex-end"
    },
    countCell: {
        textAlign: "right",
        fontWeight: 500,
        justify: "flex-end"
    },
    warningCountCell: {
        textAlign: "right",
        justify: "flex-end",
        fontWeight: 500,
        color: theme.palette.warning.main
    },
    actionCell: {
        textAlign: "center",
        justify: "center",
        padding: theme.spacing(1, 2) + ' !important',
    },
    root: {
        "& .BackgroundRow": {
            backgroundColor: "#fff3cd !important",
            borderLeft: "4px solid #ffc107 !important",
            "&:hover": {
                backgroundColor: "#ffeaa7 !important"
            }
        }
    },
    sectionDivider: {
        margin: theme.spacing(0, 0),
        backgroundColor: theme.palette.divider
    },
    headerFilters: {
        position: 'absolute',
        top: theme.spacing(2),        
        right: theme.spacing(3),      
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        zIndex: 1000,               
        backgroundColor: 'transparent',
        [theme.breakpoints.down('md')]: {
            display: 'none',
        }
    },
    headerFiltersMobile: {
        display: 'none', 
        [theme.breakpoints.down('md')]: {
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(1),
            padding: theme.spacing(2),
            paddingBottom: theme.spacing(1),
        },
        
        [theme.breakpoints.between('sm', 'md')]: {
            flexDirection: 'row',
            alignItems: 'center',
        }
    },
    filterInput: {
        minWidth: 200,
        [theme.breakpoints.down('md')]: {
            minWidth: '100%',
        },
        [theme.breakpoints.between('sm', 'md')]: {
            minWidth: 160,
        }
    },
    dateInput: {
        minWidth: 270,
        flexShrink: 0,
        [theme.breakpoints.down('md')]: {
            minWidth: '100%',
        },
        [theme.breakpoints.between('sm', 'md')]: {
            minWidth: 200,
        }
    },
    contentWrapper: {
        position: 'relative',
        padding: theme.spacing(0, 2, 2, 2),
    },
    sectionContainer: {
        marginBottom: theme.spacing(-4),
    },
    surfaceContainer: {
        position: 'relative',
    }
}));

export default function UnloadedInvoicesView() {
    const classes = useStyles();
    const context = useContext(AppContext);
    const history = useHistory();
    const location = useLocation();
    const tenantId = context.session!.tenant!.id;

    const query = new URLSearchParams(location.search);
    const [registeredPage, setRegisteredPage] = useState<number>(initialPage(query));
    const [registeredPageSize, setRegisteredPageSize] = useState<number>(initialPageSize(query));
    const [unregisteredPage, setUnregisteredPage] = useState<number>(1);
    const [unregisteredPageSize, setUnregisteredPageSize] = useState<number>(20);

    const [canRead] = useState(context.isGranted("NonIntegratedCfdisRead"));
    
    const [paymentMethod, setPaymentMethod] = useState<string>('PUE');
    const [error, setError] = useState<string>();
    const [openExportExcel, setOpenExportExcel] = useState<boolean>(false);
    const [openBackdrop, setOpenBackdrop] = useState<boolean>(false);
    const [startDate, setStartDate] = useState<Date>();
    const [endDate, setEndDate] = useState<Date>();
    const [success, setSuccess] = useState<string>();

    const {
        is_open: isBreakdownModalOpen,
        modal_data: breakdownModalData,
        open_modal: openBreakdownModal,
        close_modal: closeBreakdownModal
    } = useSimpleTaxBreakdownModal();

    const initialPeriod = useMemo(() => {
        const today = new Date();
        const firstDayOfPreviousMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const formatDate = (date: Date) => {
            return String(date.getDate()).padStart(2, '0') + "/" + 
                   String(date.getMonth() + 1).padStart(2, '0') + "/" + 
                   date.getFullYear();
        };
        setStartDate(firstDayOfPreviousMonth);
        setEndDate(today);
        return `${formatDate(firstDayOfPreviousMonth)} - ${formatDate(today)}`;
    }, []);

    const [periodForInput, setPeriodForInput] = useState<string>(initialPeriod);

    const onRegisteredChangedPage = (page: number) => {
        setRegisteredPage(page);
    };

    const onRegisteredChangedPageSize = (page: number, pageSize: number) => {
        setRegisteredPage(page);
        setRegisteredPageSize(pageSize);
    };

    const onUnregisteredChangedPage = (page: number) => {
        setUnregisteredPage(page);
    };

    const onUnregisteredChangedPageSize = (page: number, pageSize: number) => {
        setUnregisteredPage(page);
        setUnregisteredPageSize(pageSize);
    };
    
    const [period, setPeriod] = useState<string>(initialPeriod);

    const apiFilters = useMemo(() => {
        const effectivePeriod = period || initialPeriod;
        
        const dates = effectivePeriod.split(' - ');
        
        const parseDate = (dateStr: string): Date => {
            const [day, month, year] = dateStr.split('/').map(Number);
            const date = new Date(year, month - 1, day); 
            
            if (isNaN(date.getTime())) {
                console.error(translate("cfdis.cfdis_non_integrated.messages.invalid_date"), dateStr);
                return new Date();
            }
            
            return date;
        };
        
        const filters = {
            paymentMethod: paymentMethod as "PUE" | "PPD",
            startDate: parseDate(dates[0]),
            endDate: parseDate(dates[1])
        };
        
        return filters;
    }, [paymentMethod, period, initialPeriod]);

    const { 
        data: cardsData, 
        loading: cardsLoading, 
        error: cardsError 
    } = useRegisteredProvidersCards(tenantId, canRead ? apiFilters : null);

    const registeredOffset = useMemo(() => getOffset(registeredPage, registeredPageSize), [registeredPage, registeredPageSize]);
    const { 
        data: gridData, 
        loading: gridLoading, 
        error: gridError 
    } = useRegisteredProvidersGrid(tenantId, canRead ? apiFilters : null, registeredOffset, registeredPageSize);

    const { 
        data: unregisteredCardsData, 
        loading: unregisteredCardsLoading, 
        error: unregisteredCardsError 
    } = useUnregisteredIssuersCards(tenantId, canRead ? apiFilters : null);

    const unregisteredOffset = useMemo(() => getOffset(unregisteredPage, unregisteredPageSize), [unregisteredPage, unregisteredPageSize]);
    const { 
        data: unregisteredGridData, 
        loading: unregisteredGridLoading, 
        error: unregisteredGridError 
    } = useUnregisteredIssuersGrid(tenantId, canRead ? apiFilters : null, unregisteredOffset, unregisteredPageSize);

    const formatDateForURL = (date: Date): string => {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    const onUnregisteredIssuerClick = (issuer: any) => {
        const searchParams = new URLSearchParams({
            paymentMethod: apiFilters.paymentMethod,
            startDate: formatDateForURL(apiFilters.startDate),
            endDate: formatDateForURL(apiFilters.endDate)
        });
        const issuerRfc = encodeURIComponent(issuer.rfc_emisor);
        history.push(`/comprobantes/facturas-no-cargadas/emisor/${issuerRfc}/${issuerRfc}?${searchParams}`);
    };

    const onPaymentMethodChanged = (name: string, value: string) => {
        setPaymentMethod(value);
    };

    const dateRangeInputRef = useRef<any>(null);

    const [inputKey, setInputKey] = useState(0);

    const onPeriodChanged = (startDate?: Date, endDate?: Date, formatted?: string) => {    
        if (!startDate && !endDate && !formatted && periodForInput === initialPeriod) {     
            const dates = initialPeriod.split(' - ');
            const parseDate = (dateStr: string): Date => {
                const [day, month, year] = dateStr.split('/').map(Number);
                return new Date(year, month - 1, day);
            };
            
            setPeriodForInput(initialPeriod);
            setPeriod(initialPeriod);
            setStartDate(parseDate(dates[0]));
            setEndDate(parseDate(dates[1]));
            
            setTimeout(() => {
                setInputKey(prev => prev + 1);
            }, 0);
            
            return;
        }

        if (!startDate && !endDate && !formatted) {
            setStartDate(undefined);
            setEndDate(undefined);
            setError(undefined);
            setPeriod(initialPeriod);
            setPeriodForInput(initialPeriod);
            return;
        }

        if (!startDate || !endDate || !formatted) {
            return;
        }

        const today = new Date();
        const threeMonthsAgo = new Date(today.getFullYear(), today.getMonth() - 3, today.getDate());
        
        if (startDate < threeMonthsAgo) {
            setError(translate("cfdis.cfdis_non_integrated.messages.period_exceeds_limit") as string);
            setPeriodForInput("");
            setTimeout(() => {
                setPeriod(initialPeriod);
                setPeriodForInput(initialPeriod);
                setStartDate(undefined);
                setEndDate(undefined);
            }, 10);
            return;
        }
        
        if (endDate > today) {
            setError(translate("cfdis.cfdis_non_integrated.messages.end_date_future") as string);
            setPeriodForInput("");
            setTimeout(() => {
                setPeriod(initialPeriod);
                setPeriodForInput(initialPeriod);
                setStartDate(undefined);
                setEndDate(undefined);
            }, 10);
            return;
        }

        setStartDate(startDate);
        setEndDate(endDate);
        setError(undefined);
        setPeriod(formatted);
        setPeriodForInput(formatted);
    };

    const onDownloadExcel = () => {
        setOpenExportExcel(true);
    };

    const onDownloadWithConnector = (connectorId: string) => {
        setOpenBackdrop(true);
        setOpenExportExcel(false);
        let params = {
            connectorId: "",
            startDate: startDate,
            endDate: endDate,
            paymentMethod: paymentMethod
        } as NonIntegratedCfdiParams;
        params.connectorId = connectorId;
        exportExcel(context.session!.tenant!.id, params).then((response) => {
            setOpenBackdrop(false);
            if (response.url) {
                window.open(response.url, "_blank")
            } else {
                setSuccess(translate("cfdis.cfdis_non_integrated.email_export") as string);
            }
        }).catch((error) => {
            setError(error.message);
        }).finally(() => {
            setOpenBackdrop(false);
        });
    }

    const onCloseExporter = () => {
        setOpenExportExcel(false);
    }

    const onCloseSnackbars = () => {
        setSuccess(undefined);
        setError(undefined);
    };

    const hasRegisteredResults = useMemo(() => hasDataResults(cardsData), [cardsData]);

    const hasUnregisteredResults = useMemo(() => hasDataResults(unregisteredCardsData), [unregisteredCardsData]);

    const hasResults = hasRegisteredResults || hasUnregisteredResults;

    const getPaymentMethodText = (method: string) => {
        return method === 'PUE' 
            ? translate("cfdis.cfdis_non_integrated.filters.optionPUEtext") as string
            : translate("cfdis.cfdis_non_integrated.filters.optionPPDtext") as string;
    };

    const onViewTrasladosBreakdown = () => {
        openBreakdownModal(
            'transfers',
            'general',
            cardsData?.trasladosTotales.breakdown,
            apiFilters.paymentMethod,
            period.split(' - ')[0],
            period.split(' - ')[1]
        );
    };

    const onViewRetencionesBreakdown = () => {
        openBreakdownModal(
            'withholdings',
            'general',
            cardsData?.retencionesTotales.breakdown,
            apiFilters.paymentMethod,
            period.split(' - ')[0],
            period.split(' - ')[1]
        );
    };

    const onViewUnregisteredTrasladosBreakdown = () => {
        openBreakdownModal(
            'transfers',
            'unregistered',
            unregisteredCardsData?.trasladosTotales.breakdown,
            apiFilters.paymentMethod,
            period.split(' - ')[0],
            period.split(' - ')[1]
        );
    };

    const onViewUnregisteredRetencionesBreakdown = () => {
        openBreakdownModal(
            'withholdings',
            'unregistered',
            unregisteredCardsData?.retencionesTotales.breakdown,
            apiFilters.paymentMethod,
            period.split(' - ')[0],
            period.split(' - ')[1]
        );
    };

    const onProviderClick = (provider: any) => {        
        localStorage.setItem('unloadedInvoicesFilters', JSON.stringify({
            paymentMethod: apiFilters.paymentMethod,
            startDate: apiFilters.startDate.toISOString(),
            endDate: apiFilters.endDate.toISOString()
        }));

        history.push(`/comprobantes/facturas-no-cargadas/emisor/${provider.rfc_emisor}/${provider.provider_id}`);
    };

    const onUploadCfdi = (nonRelatedCfdi : NonIntegratedCfdi) => {
        console.log("Abrir modal desglose traslados - proveedores registrados", cardsData?.trasladosTotales.breakdown);
        // TODO: Implementar modal
    };

    return (
        <div className={classes.surfaceContainer}>
            <div className={classes.headerFilters}>
                <UnloadedInvoicesFilters
                    paymentMethod={paymentMethod}
                    period={periodForInput}
                    canRead={canRead}
                    cardsLoading={cardsLoading}
                    hasResults={hasResults}
                    onPaymentMethodChanged={onPaymentMethodChanged}
                    onPeriodChanged={onPeriodChanged}
                    onDownloadExcel={onDownloadExcel}
                    filterInputClass={classes.filterInput}
                    dateInputClass={classes.dateInput}
                    idSuffix=""
                    inputKey={inputKey} 
                />
            </div>

            <Surface 
                title={translate("cfdis.cfdis_non_integrated.title") as string}
                icon={<ReceiptIcon />}
                className="PaperPagination"
            >
                <div className={classes.headerFiltersMobile}>
                    <UnloadedInvoicesFilters
                        paymentMethod={paymentMethod}
                        period={periodForInput}
                        initialPeriod={initialPeriod}
                        canRead={canRead}
                        cardsLoading={cardsLoading}
                        hasResults={hasResults}
                        onPaymentMethodChanged={onPaymentMethodChanged}
                        onPeriodChanged={onPeriodChanged}
                        onDownloadExcel={onDownloadExcel}
                        filterInputClass={classes.filterInput}
                        dateInputClass={classes.dateInput}
                        idSuffix="Mobile"
                    />
                </div>
                <div className={classes.contentWrapper}>
                    <Box className={classes.root}>
                        {!canRead && (
                            <Box py={4} textAlign="center">
                                <Typography variant="body1" color="textSecondary">
                                    {translate("cfdis.cfdis_non_integrated.messages.no_permissions") as string}
                                </Typography>
                            </Box>
                        )}

                        {canRead && !cardsLoading && !unregisteredCardsLoading && !cardsError && !unregisteredCardsError && !hasResults && (
                            <Box py={4} textAlign="center">
                                <Typography variant="body1" color="textSecondary">
                                    {(translate("cfdis.cfdis_non_integrated.messages.no_results", {
                                        method: getPaymentMethodText(paymentMethod),
                                        startDate: period.split(' - ')[0],
                                        endDate: period.split(' - ')[1]
                                    }) as string)}
                                </Typography>
                            </Box>
                        )}

                        {canRead && (cardsLoading || unregisteredCardsLoading) && (
                            <Box py={4} textAlign="center">
                                <Typography variant="body1" color="textSecondary">
                                    {translate("cfdis.cfdis_non_integrated.messages.loading") as string}
                                </Typography>
                            </Box>
                        )}

                        {canRead && !cardsLoading && !cardsError && hasRegisteredResults && (
                            <div className={classes.sectionContainer}>
                                <Box py={3}>
                                    <Box px={2} pb={2}>
                                        <Grid container alignItems="center" spacing={1}>
                                            <Grid item>
                                                <GroupIcon color="primary" />
                                            </Grid>
                                            <Grid item>
                                                <Typography variant="h6">
                                                    {translate("cfdis.cfdis_non_integrated.sections.registered_providers.title") as string}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                        <Typography variant="body2" color="textSecondary">
                                            {translate("cfdis.cfdis_non_integrated.sections.registered_providers.subtitle") as string}
                                        </Typography>
                                    </Box>

                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6} md={6} lg={3}>
                                            <InformativeCard
                                                title={translate("cfdis.cfdis_non_integrated.cards.total_transfers") as string}
                                                amounts={cardsData?.trasladosTotales.amounts}
                                                cancellationAmounts={cardsData?.trasladosTotales.cancellationAmounts}
                                                hasDiscrepancy={cardsData?.trasladosTotales.hasDiscrepancy || false}
                                                showBreakdownButton={true}
                                                onViewBreakdown={onViewTrasladosBreakdown}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={6} lg={3}>
                                            <InformativeCard
                                                title={translate("cfdis.cfdis_non_integrated.cards.total_retentions") as string}
                                                amounts={cardsData?.retencionesTotales.amounts}
                                                cancellationAmounts={cardsData?.retencionesTotales.cancellationAmounts}
                                                hasDiscrepancy={cardsData?.retencionesTotales.hasDiscrepancy || false}
                                                showBreakdownButton={true}
                                                onViewBreakdown={onViewRetencionesBreakdown}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={6} lg={3}>
                                            <InformativeCard
                                                title={translate("cfdis.cfdis_non_integrated.cards.providers") as string}
                                                count={cardsData?.proveedores.count}
                                                cancellationCount={cardsData?.proveedores.cancellationCount}
                                                secondaryText={translate("cfdis.cfdis_non_integrated.cards.blocked") as string}
                                                hasDiscrepancy={cardsData?.proveedores.hasDiscrepancy || false}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={6} lg={3}>
                                            <InformativeCard
                                                title={translate("cfdis.cfdis_non_integrated.cards.invoices") as string}
                                                count={cardsData?.facturas.count}
                                                cancellationCount={cardsData?.facturas.cancellationCount}
                                                secondaryText={translate("cfdis.cfdis_non_integrated.cards.cancellation_requests") as string}
                                                hasDiscrepancy={cardsData?.facturas.hasDiscrepancy || false}
                                            />
                                        </Grid>
                                    </Grid>

                                    <Box py={2}>
                                        <Box px={2} pb={2}>
                                            <Typography variant="h6" style={{ fontWeight: 600, marginBottom: 8 }}>
                                                {translate("cfdis.cfdis_non_integrated.sections.registered_providers.grid_title") as string}
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary">
                                                {translate("cfdis.cfdis_non_integrated.sections.registered_providers.grid_subtitle") as string}
                                            </Typography>
                                        </Box>

                                        <Pagination 
                                            title=""
                                            icon={<></>}
                                            variant="embeded"
                                            page={registeredPage}
                                            pageSize={registeredPageSize}
                                            count={gridData ? gridData.items.length : 0}
                                            total={gridData ? gridData.total_elements : 0}
                                            onChangedPage={onRegisteredChangedPage}
                                            onChangedPageSize={onRegisteredChangedPageSize}
                                            hideFooterActions={!gridData || gridData.total_elements === 0}
                                        >
                                            <Gridable
                                                items={gridData ? gridData.items : []}
                                                loading={gridLoading}
                                                error={gridError || undefined}
                                                empty={translate("cfdis.cfdis_non_integrated.messages.no_providers_found") as string}
                                                onClick={onProviderClick}
                                                background={(provider: any) => {
                                                    return provider?.is_blocked || false;
                                                }}
                                                columns={[
                                                    {
                                                        title: translate("cfdis.cfdis_non_integrated.columns.business_name") as string,
                                                        converter: (provider: any) => (
                                                            <Link
                                                                component="button"
                                                                variant="body2"
                                                                className={classes.clickableCell}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    onProviderClick(provider);
                                                                }}
                                                            >
                                                                {provider.razon_social || `Proveedor ${provider.rfc_emisor}` || "Sin nombre"}
                                                            </Link>
                                                        ),
                                                        xs: 8, sm: 5, md: 5, lg: 5,
                                                        justify: "flex-start"
                                                    },
                                                    {
                                                        title: translate("cfdis.cfdis_non_integrated.columns.issuer_rfc") as string,
                                                        converter: (provider: any) => (
                                                            <span className={classes.rfcCell}>
                                                                {provider.rfc_emisor}
                                                            </span>
                                                        ),
                                                        xs: false, sm: 2, md: 2, lg: 2,
                                                        className: classes.rfcCell,
                                                        justify: "flex-start"
                                                    },
                                                    {
                                                        title: translate("cfdis.cfdis_non_integrated.columns.invoice_count") as string,
                                                        converter: (provider: any) => (
                                                            <span className={classes.countCell}>
                                                                {provider.invoice_count?.toLocaleString('es-MX') || 0}
                                                            </span>
                                                        ),
                                                        xs: false, sm: 1, md: 1, lg: 1,
                                                        className: classes.countCell,
                                                        justify: "flex-end"
                                                    },
                                                    {
                                                        title: translate("cfdis.cfdis_non_integrated.columns.cancellation_requests") as string,
                                                        converter: (provider: any) => (     
                                                            <span className={classes.countCell}>
                                                                {provider.cancellation_requests?.toLocaleString('es-MX') || 0}
                                                            </span>
                                                        ),
                                                        xs: false, sm: 2, md: 2, lg: 2,
                                                        className: classes.countCell,
                                                        justify: "flex-end"
                                                    },
                                                    {
                                                        title: translate("cfdis.cfdis_non_integrated.columns.total_unreported") as string,
                                                        converter: (provider: any) => (
                                                            <span className={classes.amountCell}>
                                                                {provider.total_no_reportado}
                                                            </span>
                                                        ),
                                                        xs: false, sm: 2, md: 2, lg: 2,
                                                        className: classes.countCell,
                                                        justify: "flex-end"
                                                    }
                                                ]}
                                            />
                                        </Pagination>
                                    </Box>
                                </Box>
                            </div>
                        )}

                    {canRead && !unregisteredCardsLoading && !unregisteredCardsError && hasUnregisteredResults && (
                            <div className={classes.sectionContainer}>
                                <Divider className={classes.sectionDivider} />
                                <Box py={3}>
                                    <Box px={2} pb={2}>
                                        <Grid container alignItems="center" spacing={1}>
                                            <Grid item>
                                                <PersonAddDisabledIcon color="primary" />
                                            </Grid>
                                            <Grid item>
                                                <Typography variant="h6">
                                                    {translate("cfdis.cfdis_non_integrated.sections.unregistered_issuers.title") as string}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                        <Typography variant="body2" color="textSecondary">
                                            {translate("cfdis.cfdis_non_integrated.sections.unregistered_issuers.subtitle") as string}
                                        </Typography>
                                    </Box>

                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <InformativeCard
                                                title={translate("cfdis.cfdis_non_integrated.cards.total_transfers") as string}
                                                amounts={unregisteredCardsData?.trasladosTotales.amounts}
                                                cancellationAmounts={undefined} 
                                                hasDiscrepancy={false} 
                                                showBreakdownButton={true}
                                                onViewBreakdown={onViewUnregisteredTrasladosBreakdown}
                                            />
                                        </Grid>
                                        
                                        <Grid item xs={12} sm={6} md={3}>
                                            <InformativeCard
                                                title={translate("cfdis.cfdis_non_integrated.cards.total_retentions") as string}
                                                amounts={unregisteredCardsData?.retencionesTotales.amounts}
                                                cancellationAmounts={undefined}
                                                hasDiscrepancy={false} 
                                                showBreakdownButton={true}
                                                onViewBreakdown={onViewUnregisteredRetencionesBreakdown}
                                            />
                                        </Grid>
                                        
                                        <Grid item xs={12} sm={6} md={3}>
                                            <InformativeCard
                                                title={translate("cfdis.cfdis_non_integrated.cards.issuers") as string}
                                                count={unregisteredCardsData?.emisores.count}
                                                cancellationCount={undefined} 
                                                hasDiscrepancy={false}
                                            />
                                        </Grid>
                                        
                                        <Grid item xs={12} sm={6} md={3}>
                                            <InformativeCard
                                                title={translate("cfdis.cfdis_non_integrated.cards.invoices") as string}
                                                count={unregisteredCardsData?.facturas.count}
                                                cancellationCount={undefined}
                                                hasDiscrepancy={false}
                                            />
                                        </Grid>
                                    </Grid>

                                    <Box py={2}>
                                        <Box px={2} pb={2}>
                                            <Typography variant="h6" style={{ fontWeight: 600, marginBottom: 8 }}>
                                                {translate("cfdis.cfdis_non_integrated.sections.unregistered_issuers.grid_title") as string}
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary">
                                                {translate("cfdis.cfdis_non_integrated.sections.unregistered_issuers.grid_subtitle") as string}
                                            </Typography>
                                        </Box>

                                        <Pagination 
                                            title=""
                                            icon={<></>}
                                            variant="embeded"
                                            page={unregisteredPage}
                                            pageSize={unregisteredPageSize}
                                            count={unregisteredGridData ? unregisteredGridData.items.length : 0}
                                            total={unregisteredGridData ? unregisteredGridData.totalElements : 0}
                                            onChangedPage={onUnregisteredChangedPage}
                                            onChangedPageSize={onUnregisteredChangedPageSize}
                                            hideFooterActions={!unregisteredGridData || unregisteredGridData.totalElements === 0}
                                        >
                                            <Gridable
                                                items={unregisteredGridData ? unregisteredGridData.items : []}
                                                loading={unregisteredGridLoading}
                                                error={unregisteredGridError || undefined}
                                                empty={translate("cfdis.cfdis_non_integrated.messages.no_issuers_found") as string}
                                                onClick={onUnregisteredIssuerClick}
                                                background={() => false}
                                                columns={[
                                                    {
                                                        title: translate("cfdis.cfdis_non_integrated.columns.business_name") as string,
                                                        converter: (issuer: any) => (
                                                            <Link
                                                                component="button"
                                                                variant="body2"
                                                                className={classes.clickableCell}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    onUnregisteredIssuerClick(issuer);
                                                                }}
                                                            >
                                                                {issuer.razon_social || `Emisor ${issuer.rfc_emisor}` || "Sin nombre"}
                                                            </Link>
                                                        ),
                                                        xs: 12, sm: 3, md: 3, lg: 3,
                                                        justify: "flex-start"
                                                    },
                                                    {
                                                        title: translate("cfdis.cfdis_non_integrated.columns.issuer_rfc") as string,
                                                        converter: (issuer: any) => (
                                                            <span className={classes.rfcCell}>
                                                                {issuer.rfc_emisor}
                                                            </span>
                                                        ),
                                                        xs: false, sm: 2, md: 2, lg: 2,
                                                        className: classes.rfcCell,
                                                        justify: "flex-start"
                                                    },
                                                    {
                                                        title: translate("cfdis.cfdis_non_integrated.columns.invoice_count") as string,
                                                        converter: (issuer: any) => (
                                                            <span className={classes.countCell}>
                                                                {issuer.invoice_count?.toLocaleString('es-MX') || 0}
                                                            </span>
                                                        ),
                                                        xs: false, sm: 2, md: 2, lg: 2, 
                                                        className: classes.countCell,
                                                        justify: "flex-end"
                                                    },
                                                    {
                                                        title: translate("cfdis.cfdis_non_integrated.columns.total_unreported") as string,
                                                        converter: (issuer: any) => (
                                                            <span className={classes.amountCell}>
                                                                {issuer.total_no_reportado}
                                                            </span>
                                                        ),
                                                        xs: false, sm: 3, md: 3, lg: 3,
                                                        className: classes.countCell,
                                                        justify: "flex-end"
                                                    },
                                                    {
                                                        title: translate("cfdis.cfdis_non_integrated.actions.add_provider") as string,
                                                        converter: (issuer: any) => (
                                                            <Tooltip title={translate("cfdis.cfdis_non_integrated.tooltips.add_provider") as string}>
                                                                <PersonAddIcon 
                                                                    style={{ 
                                                                        cursor: 'pointer',
                                                                        color: '#9e9e9e',
                                                                        transition: 'color 0.2s ease'
                                                                    }}
                                                                    onMouseEnter={(e: React.MouseEvent<SVGSVGElement>) => {
                                                                        e.currentTarget.style.color = '#1976d2';
                                                                    }}
                                                                    onMouseLeave={(e: React.MouseEvent<SVGSVGElement>) => {
                                                                        e.currentTarget.style.color = '#9e9e9e';
                                                                    }}
                                                                    onClick={(e: React.MouseEvent<SVGSVGElement>) => {
                                                                        e.stopPropagation();
                                                                        onAddProvider(issuer, history);
                                                                    }}
                                                                />
                                                            </Tooltip>
                                                        ),
                                                        xs: false, sm: 2, md: 2, lg: 2,
                                                        className: classes.actionCell,
                                                        justify: "center"
                                                    }
                                                ]}
                                            />
                                        </Pagination>
                                    </Box>
                                </Box>
                            </div>
                        )}

                        <ErrorSnackbar 
                            message={error || cardsError || unregisteredCardsError || gridError || unregisteredGridError} 
                            onClose={onCloseSnackbars} 
                        />

                        {openExportExcel && (
                            <ConnectorObjectExporter
                                tenantId={context.session!.tenant!.id}
                                family={Family.NON_INTEGRATED_CFDIS}
                                type={"DATA"}
                                onClose={onCloseExporter}
                                onExport={onDownloadWithConnector} 
                            />
                        )}

                        <SuccessSnackbar message={success} onClose={onCloseSnackbars} />
                        <CustomBackdrop open={openBackdrop} />

                        {breakdownModalData && (
                            <TaxBreakdownModal
                                open={isBreakdownModalOpen}
                                type={breakdownModalData.type}
                                context={breakdownModalData.context}
                                data={breakdownModalData.data}
                                paymentMethod={breakdownModalData.payment_method}
                                startDate={breakdownModalData.start_date}
                                endDate={breakdownModalData.end_date}
                                issuerName={breakdownModalData.issuer_name}
                                onClose={closeBreakdownModal}
                            />
                        )}
                    </Box>
                </div>
            </Surface>
        </div>
    );
}